import { Injectable, Signal, WritableSignal, signal } from '@angular/core';
import { catchError } from 'rxjs';
import { HttpHeaders, HttpClient } from '@angular/common/http';

import { environment } from '../../../environments/environment';
import { UserAuthenticationDataManagerService } from '../user-authentication-data-manager-service/user-authentication-data-manager.service';
import { AuthenticationData } from 'src/app/auth/auth-data/auth-data';
import { Apollo } from 'apollo-angular';
import { UserAuth } from 'src/app/entities/user-auth/user-auth';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';


/**
 * Model of the login query response.
 */
interface LoginResponseDto {

  // Auth token to access the app queries.
  access_token: string;

  csrf_token: string;

  logout_token: string;

  current_user: {
    uid: string;
    name: string;
  }
}


/**
 * Path to the backend auth service.
 */
const loginPath = '/user/login?_format=json';


const logoutPath = '/user/logout?_format=json&token=';


/**
 * Service that manages the authentication of the app and user data.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {


  /**
   * Signal that stores the user data like the name/username.
   */
  protected _user: WritableSignal<UserAuth | null> = signal(null);


  /**
   * Signal that stores the authentication data, like the token or exp date.
   */
  protected _authData: WritableSignal<AuthenticationData | null> = signal(null);


  /**
   * Signal that indicates if the user is logged in or not.
   */
  protected _loggedIn = signal(false);


  /**
   * Base username
   */
  _username = "";


  /**
   * Constructor that runs when the service is loaded or injected.
   *
   * It retrieves the user auth data and runs the checker function that
   * checks if the user is actually logged in.
   */
  constructor(
    protected http: HttpClient,
    protected storage: UserAuthenticationDataManagerService,
    protected apollo: Apollo,
    protected snackbarService: SnackbarService,
  ) {

    const authData = this.storage.retrieve();

    if (authData) {
      this._onAuthDataChange(authData);
    }
  }


  /***********************/
  /**  Public interface **/
  /***********************/


  /**
   * Gets the `_loggedIn` signal as read only.
   */
  get loggedInStatus(): Signal<boolean> {
    return this._loggedIn.asReadonly();
  }


  /**
   * Gets the `_authData` signal as read only.
   */
  get authData(): Signal<AuthenticationData | null> {
    return this._authData.asReadonly();
  }


  /**
   * Returns the boolean from `_loggedIn` signal.
   */
  public isUserLoggedIn(): boolean {
    return this._loggedIn();
  }


  /**
   * Function that sends the login query and returns the response.
   *
   * @param email The username of the user.
   *
   * @param password The password of the user account.
   */
  public login(email: string, password: string): void {

    this._username = email;

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    const body = JSON.stringify({
      name: email,
      pass: password
    })

    this.http
      .post<unknown>(environment.apiURL + loginPath, body.toString(), {
        headers: headers,
      })
      .pipe(
        catchError((err) => {
          console.error(err);
          this.snackbarService.showError(`${err.error.message}`);
          return [];
        })
      ).subscribe((loginResponse) => this.processLoginResponse(loginResponse as LoginResponseDto));
  }


  /**
   * Function that logout the user.
   */
  public logout(): void {
    const authData = this.storage.retrieve();

    const headers = new HttpHeaders({
      'X-CSFR-TOKEN': `${authData?.csrf}`,
    });
    const body = JSON.stringify({
    })

    this.http
      .post<unknown>(environment.apiURL + logoutPath + authData?.logout, body.toString(), {
        headers: headers,
      })
      .pipe(
        catchError((err) => {
          console.error(err);
          this.snackbarService.showError(`${err.error.message}`);
          return [];
        })
      ).subscribe(() => {

        this._onUserLogout();
        this._onAuthDataChange(this.storage.retrieve());
      });


    this._onUserLogout();
    this._onAuthDataChange(this.storage.retrieve());

  }


  /**
   * Function that checks if the `access_token` has expired.
   *
   * And if it indeed expired, it will call `refreshToken` function.
   *
   * @param expirationDate Expiration date from auth data.
   *
   * @param refreshToken Token used to refresh the first token.
   */
  public checkTokenExpiration(expiration: Date): void {
    const currentDate = new Date();
    const difference = new Date(expiration).getTime() - currentDate.getTime();

    if (difference < 1) {
      this.logout()
    }
  }


  public decodeJwt(token: string) {
    const data = token.split('.')[1];
    if (data) return JSON.parse(atob(data));
    else return
  }

  /***********************/
  /**  Internal methods **/
  /***********************/


  /**
   * Function that stores the auth data after login.
   *
   * @param loginResponse The response from the login query.
   */
  protected processLoginResponse(loginResponse: LoginResponseDto): void {
    this.snackbarService.showSuccess('Logged in successfully!');

    const exp_date = new Date(this.decodeJwt(loginResponse.access_token).exp * 1000);

    const authData = new AuthenticationData(
      loginResponse.current_user.name,
      loginResponse.current_user.uid,
      loginResponse.access_token,
      loginResponse.csrf_token,
      loginResponse.logout_token,
      exp_date
    );

    this.storage.save(authData);
    this._onAuthDataChange(authData);
  }


  /**
   * Function that sets the user state as logged or not depending auth data.
   *
   * @param authData The auth data received by the login query.
   */
  protected _onAuthDataChange(authData: AuthenticationData | null): void {

    if (authData && authData !== null) {
      const user = new UserAuth(
        authData.username
      );
      this._authData.set(authData);
      this._onUserLogin(user);
    }
    else {
      this._authData.set(null);
      this._onUserLogout();
    }
  }


  /**
   * Function that sets the user as logged in and sets the user data.
   */
  protected _onUserLogin(user: UserAuth): void {
    this._loggedIn.set(true);
    this._user.set(user);
  }


  /**
   * Function that sets the user as no logged.
   *
   * Also we can say that this function is the one in charge of logout.
   */
  protected _onUserLogout(): void {
    this._loggedIn.set(false);
    this._user.set(null);
    this.storage.logOut()
    window.location.reload()
  }
}
